<template>
  <v-app-bar
    color="primary"
    height="56"
    class="z-10"
  >
    <img
      class="logo"
      :src="logo"
    >
    <v-app-bar-title class="zamg-text-headline ml-8">
      {{ t('toolbar.header') }}
    </v-app-bar-title>
    <v-spacer />
    <div class="language-switch-wrapper">
      <div class="language-switch-vertical-bar" />
      <v-select
        v-model="currentLanguage"
        :items="languages"
        item-title="label"
        item-value="code"
        density="compact"
        class="language-switch"
        hide-selected
        hide-details
        :icon-color="iconColor"
        menu-icon="mdi-chevron-down"
        bg-color="primary"
        max-width="5rem"
        variant="text"
        data-testid="language-switch"
        @update:model-value="(value) => switchToLanguage(value)"
      >
        <template #item="{props}">
          <v-list
            density="compact"
            class="language-switch-list"
          >
            <v-list-item
              v-bind="props"
              density="compact"
              class="language-switch-list-item"
            />
          </v-list>
        </template>
      </v-select>
    </div>
  </v-app-bar>
</template>

<script lang="ts">
import { defineComponent, ref } from 'vue';
import AppConfigStore from '@/stores/config/app-config-module';
import Cookies from 'js-cookie';
import logo from '@/assets/GSA_Basislogo_NegativAufMidnightGreen_RGB_XXS.png';
import { useI18n } from "vue-i18n";
import { colors } from "@/plugins/vuetify";

export default defineComponent({
  name: 'AppNavbar',
  props: {
    logo: { type: String, default: logo }
  },
  setup() {
    const { t, locale } = useI18n();

    const languages = [
      { label: 'DE', code: 'de' },
      { label: 'EN', code: 'en' },
    ] as const;
    type Language = typeof languages[number]['code'];

    const cookieDomain = AppConfigStore.cookieDomain;
    const savedLanguage = Cookies.get('preferred_language');
    const currentLanguage = ref<string>(validateLanguage(savedLanguage));
    locale.value = currentLanguage.value.toLowerCase();
    const iconColor = colors.secondary;

    function setCookie(lang: string) {
      const isLocalhost =
        !cookieDomain ||
        cookieDomain === 'localhost' ||
        cookieDomain.includes('localhost');
      Cookies.set('preferred_language', lang, {
        domain: isLocalhost ? undefined : cookieDomain,
        path: '/',
        expires: 365,
        secure: !isLocalhost,
        sameSite: 'Lax'
      });
    }

    function switchToLanguage(language: string) {
      locale.value = language.toLowerCase();
      currentLanguage.value = language;
      setCookie(language.toLowerCase());
    }

    function validateLanguage(lang?: string): Language {
      const normalized = (lang ?? '').toLowerCase().trim();

      const language_found = languages.find(l => l.code === normalized);
      return language_found ? language_found.code : 'de';
    }

    return {
      t,
      locale,
      languages,
      switchToLanguage,
      currentLanguage,
      iconColor,
    }
  }
})
</script>

<style scoped>
.float-right {
  position: absolute;
  right: 0;
  top: 0;
}
.z-10 {
  z-index: 10 !important;
}
.logo {
  max-height: 50px;
  max-width: 120px;
  margin: 25px;
}
.language-switch-wrapper {
  display: flex;
  align-items: center;
  gap: 0px;
}
.language-switch-vertical-bar {
  width: 2px;
  height: 1.5rem;
  background-color: rgb(var(--v-theme-secondary));
}
.language-switch {
  margin-right: 20px;
}
.language-switch-list {
  padding-top: 0px !important;
  padding-bottom: 0px !important;
}
.language-switch-list-item {
  padding-top: 0px !important;
  padding-bottom: 0px !important;
  max-height: 2rem;
  min-height: 2rem;
  height: 2rem;
}
:deep(.language-switch .v-field) {
  padding-inline: 0px !important;
}
</style>
